import React, { useEffect, useState } from "react";
import {
  Box, Typography, Link, useMediaQuery, useTheme,
  Skeleton, Collapse,
} from "@mui/material";
import { getStocksGraph, getRecentlyViewed } from "../../../api/api";
import { getStockNews } from "../../../api/stocks_api";
import RecentlyViewedWidget from "../Widgets/recentlyViewedBox";
import BusinessIcon from "@mui/icons-material/Business";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ProStockChart from "../Widgets/ProStockChart";
import { getCurrencySymbol, fmtL } from "../../../utils/currency";

/* ══════════════════════════════════════════════
   SHARED PRIMITIVES
══════════════════════════════════════════════ */

const SectionIcon = ({ children, gradient, border }) => (
  <Box sx={{
    width: 40, height: 40, borderRadius: 2, flexShrink: 0,
    background: gradient, border: `1px solid ${border}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "var(--shadow-md)",
  }}>
    {children}
  </Box>
);

const SectionHeader = ({ icon, title, subtitle, end }) => (
  <Box sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: { xs: "flex-start", sm: "center" },
    mb: 2.5,
    gap: 1.5,
    flexWrap: "wrap",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {icon}
      <Box>
        <Typography sx={{
          fontSize: "var(--font-size-base)",
          fontWeight: "var(--font-weight-bold)",
          color: "var(--color-text-primary)",
          display: "block",
        }}>
          {title}
        </Typography>
        <Typography sx={{
          fontSize: "var(--font-size-xs2)",
          color: "var(--color-text-muted)",
          fontWeight: "var(--font-weight-medium)",
          display: "block",
        }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
    {end && (
      <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
        {end}
      </Box>
    )}
  </Box>
);

const Card = ({ children, sx = {} }) => (
  <Box sx={{
    p: { xs: 2, sm: 3 },
    background: "linear-gradient(135deg, var(--color-surface) 0%, var(--color-bg-secondary) 100%)",
    border: "1px solid var(--color-border-primary)",
    borderRadius: "var(--radius-xl)",
    boxShadow: "var(--shadow-xl)",
    ...sx,
  }}>
    {children}
  </Box>
);

/* ══════════════════════════════════════════════
   INFO ROW
══════════════════════════════════════════════ */
const InfoRow = ({ label, value, children }) => (
  <Box sx={{
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    py: 1.5, px: 1, mx: -1, borderRadius: 1,
    borderBottom: "1px solid var(--color-border-primary)",
    "&:last-child": { borderBottom: "none" },
    transition: "var(--transition-fast)",
    "&:hover": { bgcolor: "var(--color-surface-hover)" },
  }}>
    <Typography sx={{
      fontSize: "var(--font-size-xs2)", fontWeight: "var(--font-weight-semibold)",
      color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em",
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontSize: "var(--font-size-xs2)", color: "var(--color-text-primary)",
      textAlign: "right", maxWidth: "60%", fontWeight: "var(--font-weight-medium)",
    }}>
      {children || value || "—"}
    </Typography>
  </Box>
);

/* ══════════════════════════════════════════════
   NEWS ARTICLE CARD
══════════════════════════════════════════════ */
const NewsArticle = ({ article }) => {
  const formatDate = (ds) => {
    if (!ds) return "";
    const d = new Date(ds), now = new Date(), ms = now - d;
    const m = Math.floor(ms / 60000), h = Math.floor(ms / 3_600_000), day = Math.floor(ms / 86_400_000);
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <Box
      component="a"
      href={article.url || article.article_url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        p: 2, borderRadius: "var(--radius-lg)",
        border: "1px solid var(--color-border-primary)",
        background: "var(--color-surface)",
        display: "flex", flexDirection: "column", gap: 1,
        textDecoration: "none", transition: "var(--transition-normal)",
        "&:hover": {
          borderColor: "var(--color-primary)",
          bgcolor: "var(--color-surface-hover)",
          transform: "translateY(-2px)",
          boxShadow: "var(--shadow-lg)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{
          px: 1, py: 0.25, borderRadius: "var(--radius-sm)",
          bgcolor: "var(--color-primary-lighter)",
          border: "1px solid var(--color-primary)",
        }}>
          <Typography sx={{ fontSize: "var(--font-size-xs2)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-primary)" }}>
            {article.source || article.publisher || "News"}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "var(--font-size-xs2)", color: "var(--color-text-muted)", fontWeight: "var(--font-weight-medium)" }}>
          {formatDate(article.published_at || article.date)}
        </Typography>
      </Box>

      <Typography sx={{
        fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)",
        color: "var(--color-text-primary)", lineHeight: 1.55,
      }}>
        {article.title}
      </Typography>

      {article.summary && (
        <Typography sx={{
          fontSize: "var(--font-size-xs2)", color: "var(--color-text-secondary)",
          lineHeight: 1.6,
          display: { xs: "none", sm: "-webkit-box" },
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {article.summary}
        </Typography>
      )}
    </Box>
  );
};

/* ══════════════════════════════════════════════
   CHART CONTROLS
══════════════════════════════════════════════ */
const ChartControls = ({ chartType, setChartType, chartPeriod, setChartPeriod }) => (
  <Box sx={{
    display: "flex",
    alignItems: "center",
    gap: { xs: 0.3, sm: 0.6 },
    bgcolor: "var(--color-bg-secondary)",
    px: { xs: 0.6, sm: 1 },
    py: 0.6,
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--color-border-primary)",
    overflowX: "auto",
    width: "100%",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  }}>
    {/* Chart type icons */}
    {[
      { t: "line", Icon: ShowChartIcon },
      { t: "candlestick", Icon: CandlestickChartIcon },
    ].map(({ t, Icon }) => (
      <Box
        key={t}
        onClick={() => setChartType(t)}
        sx={{
          width: { xs: 26, sm: 30 },
          height: { xs: 26, sm: 30 },
          borderRadius: "var(--radius-sm)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
          transition: "var(--transition-fast)",
          bgcolor: chartType === t ? "var(--color-surface-active)" : "transparent",
          color: chartType === t ? "var(--color-primary)" : "var(--color-text-secondary)",
          "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-primary)" },
        }}
      >
        <Icon sx={{ fontSize: { xs: 15, sm: 18 } }} />
      </Box>
    ))}

    {/* Divider */}
    <Box sx={{ width: "1px", height: 16, bgcolor: "var(--color-border-primary)", flexShrink: 0, mx: 0.3 }} />

    {/* Period buttons */}
    {["1D", "5D", "1M", "6M", "YTD", "1Y"].map((p) => (
      <Box
        key={p}
        onClick={() => setChartPeriod(p)}
        sx={{
          px: { xs: 0.8, sm: 1.2 },
          py: { xs: 0.3, sm: 0.4 },
          fontSize: { xs: "var(--font-size-xs2)", sm: "var(--font-size-sm)" },
          fontWeight: "var(--font-weight-bold)",
          borderRadius: "var(--radius-sm)",
          cursor: "pointer",
          flexShrink: 0,
          transition: "var(--transition-fast)",
          whiteSpace: "nowrap",
          userSelect: "none",
          color: chartPeriod === p ? "var(--color-primary)" : "var(--color-text-secondary)",
          bgcolor: chartPeriod === p ? "var(--color-surface-active)" : "transparent",
          "&:hover": { color: "var(--color-text-primary)", bgcolor: "var(--color-surface-hover)" },
        }}
      >
        {p}
      </Box>
    ))}
  </Box>
);

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const Summary = ({ stock, stockPriceChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [showMore, setShowMore] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [newsArticles, setNewsArticles] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState("5D");
  const [chartType, setChartType] = useState("candlestick");
  const [loadingChart, setLoadingChart] = useState(false);
  const [periodStats, setPeriodStats] = useState(null);
  const sym = getCurrencySymbol();

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  useEffect(() => {
    if (!stock?.ticker) return;
    (async () => {
      setLoadingRecent(true);
      const res = await getRecentlyViewed();
      if (res?.success && Array.isArray(res?.data))
        setRecentlyViewed(res.data.filter((i) => i.ticker !== stock.ticker));
      setLoadingRecent(false);
    })();
  }, [stock?.ticker]);

  useEffect(() => {
    if (!stock?.ticker) return;
    (async () => {
      setLoadingNews(true);
      const res = await getStockNews(stock.ticker, 10);
      setNewsArticles(res?.success && res?.data?.items ? res.data.items : []);
      setLoadingNews(false);
    })();
  }, [stock?.ticker]);

  useEffect(() => {
    if (!stock?.ticker) return;
    (async () => {
      setLoadingChart(true);
      try {
        const res = await getStocksGraph(stock.ticker, chartPeriod);
        if (!res?.success || !Array.isArray(res?.data?.prices)) {
          setChartData([]); setPeriodStats(null); return;
        }
        setChartData(res.data.prices.map((item, idx) => ({
          idx, timestamp: item.timestamp,
          open: item.open, high: item.high, low: item.low,
          close: item.close, volume: item.volume, value: item.close,
        })));
        setPeriodStats(res.data);
      } catch { setChartData([]); }
      finally { setLoadingChart(false); }
    })();
  }, [stock?.ticker, chartPeriod]);

  if (!stock || typeof stock !== "object") {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Typography sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
          Unable to load stock details.
        </Typography>
      </Box>
    );
  }

  /* key metrics */
  const keyMetrics = [
    { label: "P/E Ratio", value: stock.p_e_ratio, fmt: (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }), icon: <AssessmentIcon sx={{ fontSize: 15, color: "#60a5fa" }} />, color: "#60a5fa" },
    { label: "Op. Margin", value: stock.operating_profit_margin ?? stock.operating_margin, fmt: (v) => `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`, icon: <TrendingUpIcon sx={{ fontSize: 15, color: "#10b981" }} />, color: "#10b981" },
    { label: "ROE", value: stock.return_on_equity ?? stock.r_o_e_margin, fmt: (v) => `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`, icon: <TrendingUpIcon sx={{ fontSize: 15, color: "#a78bfa" }} />, color: "#a78bfa" },
    { label: "Debt/Equity", value: stock.debt_to_equity, fmt: (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), icon: <AccountBalanceIcon sx={{ fontSize: 15, color: "#f59e0b" }} />, color: "#f59e0b" },
    { label: "Current Ratio", value: stock.current_ratio, fmt: (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), icon: <AssessmentIcon sx={{ fontSize: 15, color: "#06b6d4" }} />, color: "#06b6d4" },
  ].filter((m) => m.value != null && !isNaN(Number(m.value)));

  const isPositive = (stockPriceChange?.change ?? 0) >= 0;

  const statsItems = [
    { label: "CURRENT", value: stockPriceChange?.price != null ? `${sym}${fmtL(stockPriceChange.price, 2)}` : "—", isPrimary: true },
    { label: "HIGH", value: stockPriceChange?.high != null ? `${sym}${fmtL(stockPriceChange.high, 2)}` : "—", color: "var(--color-success)" },
    { label: "LOW", value: stockPriceChange?.low != null ? `${sym}${fmtL(stockPriceChange.low, 2)}` : "—", color: "var(--color-error)" },
    { label: "OPEN", value: stockPriceChange?.open != null ? `${sym}${fmtL(stockPriceChange.open, 2)}` : "—" },
    { label: "CHANGE", value: stockPriceChange?.change != null ? `${Number(stockPriceChange.change) >= 0 ? "+" : ""}${sym}${fmtL(Math.abs(stockPriceChange.change), 2)}` : "—", highlight: true },
    { label: "CHG %", value: stockPriceChange?.change_pct != null ? `${Number(stockPriceChange.change_pct) >= 0 ? "+" : ""}${fmtL(Math.abs(stockPriceChange.change_pct), 2)}%` : "—", highlight: true },
    { label: "VOLUME", value: stockPriceChange?.volume ? `${fmtL(stockPriceChange.volume / 1_000_000, 2)}M` : "—" },
  ];

  return (
    <Box sx={{
      display: "flex",
      flexDirection: { xs: "column", lg: "row" },
      gap: { xs: 2, sm: 3 },
      width: "100%",
      px: { xs: 1.5, sm: 2.5, md: 3 },
      py: { xs: 1.5, sm: 2 },
    }}>

      {/* ══════════════ MAIN COLUMN ══════════════ */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: { xs: 2, sm: 3 } }}>

        {/* ── Key Metrics ── */}
        {keyMetrics.length > 0 && (
          <Box sx={{
            display: isTablet ? "flex" : "grid",
            gridTemplateColumns: isTablet ? undefined : `repeat(${Math.min(keyMetrics.length, 5)}, 1fr)`,
            overflowX: isTablet ? "auto" : "visible",
            gap: { xs: 1.5, sm: 2 },
            pb: { xs: 0.5, md: 0 },
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}>
            {keyMetrics.map((m, idx) => (
              <Box key={idx} sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: "var(--radius-lg)",
                background: "linear-gradient(135deg, var(--color-surface) 0%, var(--color-bg-secondary) 100%)",
                border: "1px solid var(--color-border-primary)",
                transition: "var(--transition-normal)",
                flexShrink: 0,
                minWidth: { xs: 130, sm: "unset" },
                "&:hover": { borderColor: `${m.color}44`, transform: "translateY(-2px)", boxShadow: "var(--shadow-lg)" },
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: 1, bgcolor: `${m.color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {m.icon}
                  </Box>
                  <Typography sx={{ fontSize: "var(--font-size-xs2)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.3 }}>
                    {m.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "var(--font-size-xl)", fontWeight: "var(--font-weight-black)", color: "var(--color-text-primary)", fontFamily: "'SF Mono', Monaco, monospace" }}>
                  {m.fmt(m.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Chart Section ── */}
        <Card>
          <SectionHeader
            icon={
              <SectionIcon
                gradient="linear-gradient(135deg, rgba(0,150,255,0.15), rgba(99,102,241,0.15))"
                border="var(--color-primary)"
              >
                <ShowChartIcon sx={{ fontSize: 20, color: "var(--color-primary)" }} />
              </SectionIcon>
            }
            title={`${stock.ticker} Analysis`}
            subtitle="Price movement & technical overview"
            end={
              <ChartControls
                chartType={chartType}
                setChartType={setChartType}
                chartPeriod={chartPeriod}
                setChartPeriod={setChartPeriod}
              />
            }
          />

          {/* Chart canvas */}
          <Box sx={{
            height: { xs: 240, sm: 340, md: 460 },
            borderRadius: "var(--radius-lg)",
            overflow: "hidden", position: "relative",
            bgcolor: "var(--color-bg-secondary)",
            border: "1px solid var(--color-border-primary)",
            mb: 2,
          }}>
            {loadingChart ? (
              <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)" }}>
                  Loading chart data…
                </Typography>
              </Box>
            ) : chartData.length === 0 ? (
              <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)" }}>
                  No chart data available
                </Typography>
              </Box>
            ) : (
              <ProStockChart data={chartData} chartType={chartType} period={chartPeriod} />
            )}
          </Box>

          {/* Stats bar */}
          {periodStats && (
            <Box sx={{
              display: isMobile ? "flex" : "grid",
              gridTemplateColumns: isMobile ? undefined : `repeat(${statsItems.length}, 1fr)`,
              overflowX: isMobile ? "auto" : "visible",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--color-border-primary)",
              bgcolor: "var(--color-bg-secondary)",
            }}>
              {statsItems.map((item, idx) => (
                <Box key={idx} sx={{
                  textAlign: "center",
                  py: { xs: 1.2, sm: 1.6 },
                  px: { xs: 1.5, sm: 1 },
                  flexShrink: 0,
                  minWidth: { xs: 80, sm: "unset" },
                  borderRight: "1px solid var(--color-border-secondary)",
                  "&:last-child": { borderRight: "none" },
                }}>
                  <Typography sx={{ fontSize: "var(--font-size-xs2)", color: "var(--color-text-muted)", fontWeight: "var(--font-weight-bold)", letterSpacing: "0.08em", display: "block", mb: 0.3 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: item.isPrimary ? "var(--font-size-lg)" : "var(--font-size-sm)",
                    fontWeight: "var(--font-weight-bold)",
                    fontFamily: "'SF Mono', monospace",
                    color: item.highlight
                      ? isPositive ? "var(--color-success)" : "var(--color-error)"
                      : item.color || "var(--color-text-primary)",
                  }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Card>

        {/* ── News ── */}
        <Card>
          <SectionHeader
            icon={
              <SectionIcon gradient="linear-gradient(135deg, rgba(139,92,246,0.15), rgba(99,102,241,0.15))" border="rgba(139,92,246,0.3)">
                <NewspaperIcon sx={{ fontSize: 20, color: "#a78bfa" }} />
              </SectionIcon>
            }
            title="Latest News"
            subtitle="Market updates & insights"
            end={
              newsArticles.length > 0 && (
                <Box sx={{ px: 1.5, py: 0.4, borderRadius: "var(--radius-sm)", bgcolor: "var(--color-surface-hover)", border: "1px solid var(--color-border-primary)" }}>
                  <Typography sx={{ fontSize: "var(--font-size-xs2)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)" }}>
                    {newsArticles.length} articles
                  </Typography>
                </Box>
              )
            }
          />

          {loadingNews ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={110} sx={{ bgcolor: "var(--color-surface-hover)", borderRadius: "var(--radius-lg)" }} />
              ))}
            </Box>
          ) : newsArticles.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, bgcolor: "var(--color-bg-secondary)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border-primary)" }}>
              <NewspaperIcon sx={{ fontSize: 36, color: "var(--color-text-muted)", mb: 1.5 }} />
              <Typography sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", fontWeight: "var(--font-weight-medium)" }}>
                No news articles available
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              {newsArticles.map((article, i) => (
                <NewsArticle key={article.id || i} article={article} />
              ))}
            </Box>
          )}
        </Card>

        {/* ── Company Profile ── */}
        <Card>
          <SectionHeader
            icon={
              <SectionIcon gradient="linear-gradient(135deg, rgba(0,150,255,0.15), rgba(37,99,235,0.15))" border="var(--color-primary)">
                <BusinessIcon sx={{ fontSize: 20, color: "var(--color-primary)" }} />
              </SectionIcon>
            }
            title="Company Profile"
            subtitle="Overview & details"
            end={
              stock.logo_url && (
                <Box sx={{ height: 44, px: 2, display: "flex", alignItems: "center", bgcolor: "var(--color-surface-hover)", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border-primary)" }}>
                  <Box component="img" src={stock.logo_url} alt={stock.company_name} sx={{ height: 30, maxWidth: 110, objectFit: "contain" }} />
                </Box>
              )
            }
          />

          {(stock.sector_name || stock.industry_name) && (
            <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
              {stock.sector_name && (
                <Box sx={{ px: 1.5, py: 0.4, borderRadius: "var(--radius-sm)", bgcolor: "var(--color-primary)" }}>
                  <Typography sx={{ fontSize: "var(--font-size-xs2)", fontWeight: "var(--font-weight-semibold)", color: "#FFFFFF" }}>{stock.sector_name}</Typography>
                </Box>
              )}
              {stock.industry_name && (
                <Box sx={{ px: 1.5, py: 0.4, borderRadius: "var(--radius-sm)", bgcolor: "#7C3AED" }}>
                  <Typography sx={{ fontSize: "var(--font-size-xs2)", fontWeight: "var(--font-weight-semibold)", color: "#FFFFFF" }}>{stock.industry_name}</Typography>
                </Box>
              )}
            </Box>
          )}

          {stock.description && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)" }}>
              <Typography sx={{
                fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", lineHeight: 1.85,
                display: "-webkit-box",
                WebkitLineClamp: showMore ? "unset" : 4,
                WebkitBoxOrient: "vertical", overflow: "hidden",
              }}>
                {stock.description}
              </Typography>
              {stock.description?.length > 300 && (
                <Typography onClick={() => setShowMore(!showMore)} sx={{
                  fontSize: "var(--font-size-xs2)", color: "var(--color-primary)",
                  cursor: "pointer", mt: 1.5, fontWeight: "var(--font-weight-semibold)",
                  display: "inline-flex", alignItems: "center", gap: 0.5,
                  transition: "var(--transition-fast)",
                  "&:hover": { color: "var(--color-primary-light)", transform: "translateX(2px)" },
                }}>
                  {showMore ? "Show less ↑" : "Show more →"}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: { sm: 4, md: 6 },
            bgcolor: "var(--color-bg-secondary)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: "var(--radius-lg)", p: 2,
          }}>
            <Box>
              <InfoRow label="Sector" value={stock.sector_name} />
              <InfoRow label="Industry" value={stock.industry_name} />
              <InfoRow label="Employees" value={stock.employees ? stock.employees.toLocaleString() : "—"} />
            </Box>
            <Box>
              <InfoRow label="Address" value={stock.headquarters} />
              <InfoRow label="Phone" value={stock.phone} />
              <InfoRow label="Website">
                {stock.website ? (
                  <Link href={stock.website} target="_blank" rel="noopener noreferrer" sx={{
                    fontSize: "var(--font-size-xs2)", color: "var(--color-primary)", textDecoration: "none",
                    fontWeight: "var(--font-weight-semibold)", transition: "var(--transition-fast)",
                    "&:hover": { color: "var(--color-primary-light)" },
                  }}>
                    {stock.website?.replace(/^https?:\/\/(www\.)?/, "")}
                  </Link>
                ) : "—"}
              </InfoRow>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* ══════════════ SIDEBAR ══════════════ */}
      <Box sx={{
        width: { xs: "100%", lg: 320 },
        flexShrink: 0,
        position: { lg: "sticky" },
        top: { lg: 80 },
        alignSelf: { lg: "flex-start" },
        maxHeight: { lg: "calc(100vh - 100px)" },
        overflowY: { lg: "auto" },
      }}>
        {isMobile ? (
          <Box sx={{ border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
            <Box
              onClick={() => setSidebarOpen((p) => !p)}
              sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                px: 2, py: 1.5, cursor: "pointer",
                bgcolor: "var(--color-surface)",
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
              }}
            >
              <Typography sx={{ fontSize: "var(--font-size-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>
                Recently Viewed
              </Typography>
              <ExpandMoreIcon sx={{
                fontSize: 20, color: "var(--color-text-muted)",
                transform: sidebarOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "var(--transition-fast)",
              }} />
            </Box>
            <Collapse in={sidebarOpen}>
              <Box sx={{ bgcolor: "var(--color-surface)", borderTop: "1px solid var(--color-border-primary)" }}>
                <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
              </Box>
            </Collapse>
          </Box>
        ) : (
          <RecentlyViewedWidget data={recentlyViewed} loading={loadingRecent} />
        )}
      </Box>
    </Box>
  );
};

export default Summary;